// Web Push in the app (design 8.5): turning notifications on and off,
// keeping the subscription in step with the server, and tidying up shown
// notifications. The notifications themselves are shown by public/sw.js.
//
// Rules:
// - Permission is asked only after the user presses a button (browsers
//   block or quiet prompts that come without a gesture).
// - On every start the app checks its subscription: when the server's key
//   changed (a restored or new server), it subscribes again; it also sends
//   the subscription to the server again (PUT is idempotent), which heals
//   a server that lost it.
// - The choice "on" is remembered per browser profile, so a granted
//   permission without a subscription (cleared by the browser) is repaired
//   silently, but an "off" is respected.
import { api } from '../../api/client'

const PREF_KEY = 'hm.push'
const PROMPT_KEY = 'hm.push.prompt'
const PROMPT_AGAIN_MS = 14 * 24 * 3600 * 1000

export type PushStatus =
  | 'unsupported' // no service worker / Push API, or not a secure context
  | 'server-off' // the server sends no notifications
  | 'denied' // the user blocked notifications for this site
  | 'off'
  | 'on'

/** base64url (no padding) -> bytes */
export function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4)
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.length) return false
  const x = new Uint8Array(a)
  for (let i = 0; i < x.length; i++) if (x[i] !== b[i]) return false
  return true
}

/** The body of PUT /api/me/push from a browser subscription. */
export function subscriptionBody(sub: PushSubscription): { endpoint: string; p256dh: string; auth: string } | null {
  const j = sub.toJSON()
  const p256dh = j.keys?.['p256dh']
  const auth = j.keys?.['auth']
  if (!j.endpoint || !p256dh || !auth) return null
  return { endpoint: j.endpoint, p256dh, auth }
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

function rememberOn(on: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off')
  } catch {
    // storage blocked: the choice is just not remembered
  }
}

function wantedOn(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === 'on'
  } catch {
    return false
  }
}

/** The service worker registration; rejects when it does not become ready
    (a browser that blocks service workers, e.g. some private modes). */
async function registration(timeoutMs = 10_000): Promise<ServiceWorkerRegistration> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('the service worker is not ready')), timeoutMs)
  })
  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout])
  } finally {
    clearTimeout(timer)
  }
}

export async function pushStatus(publicKey: string | null): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported'
  if (!publicKey) return 'server-off'
  if (Notification.permission === 'denied') return 'denied'
  if (Notification.permission !== 'granted') return 'off'
  const sub = await (await registration()).pushManager.getSubscription()
  return sub && sameKey(sub.options.applicationServerKey, b64urlToBytes(publicKey)) ? 'on' : 'off'
}

async function subscribe(reg: ServiceWorkerRegistration, publicKey: string): Promise<void> {
  const key = b64urlToBytes(publicKey)
  let sub = await reg.pushManager.getSubscription()
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    // made for another server key: useless now
    await sub.unsubscribe().catch(() => false)
    sub = null
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  const body = subscriptionBody(sub)
  if (!body) throw new Error('the browser gave an incomplete subscription')
  await api.pushSubscribe(body)
}

/** Turns notifications on. Call from a click handler (the permission
    prompt needs a user gesture). */
export async function enablePush(publicKey: string): Promise<PushStatus> {
  const permission = await Notification.requestPermission()
  if (permission === 'denied') return 'denied'
  if (permission !== 'granted') return 'off'
  await subscribe(await registration(), publicKey)
  rememberOn(true)
  return 'on'
}

export async function disablePush(): Promise<void> {
  rememberOn(false)
  const sub = await (await registration()).pushManager.getSubscription()
  await api.pushUnsubscribe()
  await sub?.unsubscribe().catch(() => false)
}

/** At start-up (after bootstrap): repairs the subscription quietly. */
export async function syncPush(publicKey: string | null): Promise<void> {
  if (!pushSupported() || !publicKey || Notification.permission !== 'granted') return
  const reg = await registration()
  const sub = await reg.pushManager.getSubscription()
  if (!sub && !wantedOn()) return
  try {
    await subscribe(reg, publicKey)
  } catch {
    // offline or the push service is unreachable: the next start retries
  }
}

/** Closes the notifications of a room once it is on screen (design 8.5). */
export async function closeRoomNotifications(roomId: number): Promise<void> {
  if (!pushSupported()) return
  const reg = await navigator.serviceWorker.getRegistration()
  const list = await reg?.getNotifications({ tag: `room:${roomId}` })
  for (const n of list ?? []) n.close()
}

/** "Later" on the room list's prompt hides it for two weeks. */
export function promptDismissed(now = Date.now()): boolean {
  try {
    const at = Number(localStorage.getItem(PROMPT_KEY) ?? '0')
    return now - at < PROMPT_AGAIN_MS
  } catch {
    return false
  }
}

export function dismissPrompt(now = Date.now()): void {
  try {
    localStorage.setItem(PROMPT_KEY, String(now))
  } catch {
    // not remembered
  }
}

/** The number on the app icon (Windows Edge shows it; design 7.3). */
export function setAppBadge(count: number): void {
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>
    clearAppBadge?: () => Promise<void>
  }
  if (count > 0) void nav.setAppBadge?.(count).catch(() => undefined)
  else void nav.clearAppBadge?.().catch(() => undefined)
}
